import { createContext, useContext, useReducer } from 'react';
import Router from '@alltrails/shared/components/ReactRouter';
import MinimalLayout from 'components/MinimalLayout';
import { Page } from 'components/shared/Page/Page';
import CurrencyCode from 'types/CurrencyCode';
import GiftFlowRouting from '../GiftFlowRouting';

type ContextType = {
  dispatchGiftFlow: any;
  giftFlowState: any;
};

const GiftFlowContext = createContext<ContextType>({ dispatchGiftFlow: null, giftFlowState: null });

function useGiftFlowContext() {
  const context = useContext(GiftFlowContext);
  return context;
}

const initialState = {
  activePlanType: 'annualOnce',
  giftCustomizeParams: {
    backgroundId: 'gc-1',
    sendMethod: 'email',
    sendDate: new Date(),
    recipientName: '',
    recipientEmail: '',
    message: '',
    senderName: '',
    senderEmail: ''
  },
  serverErrors: [] as any,
  purchasedGiftCard: null as any
};

const UPDATE_GIFT_CUSTOMIZE_FORM = 'UPDATE_GIFT_CUSTOMIZE_FORM';
const UPDATE_SERVER_ERRORS = 'UPDATE_SERVER_ERRORS';
const UPDATE_PURCHASED_GIFT_CARD = 'UPDATE_PURCHASED_GIFT_CARD';
const RESTART_GIFT_FLOW = 'RESTART_GIFT_FLOW';

const giftFlowReducer = (state: any, action: any) => {
  switch (action.type) {
    case UPDATE_GIFT_CUSTOMIZE_FORM: {
      return { ...state, giftCustomizeParams: action.payload };
    }
    case UPDATE_SERVER_ERRORS: {
      return { ...state, serverErrors: action.payload };
    }
    case UPDATE_PURCHASED_GIFT_CARD:
      return { ...state, purchasedGiftCard: action.payload };
    case RESTART_GIFT_FLOW:
      return { ...state, ...initialState };
    default:
      return state;
  }
};

type Props = {
  currencyCode?: CurrencyCode;
  paymentTypes: any;
  giftSubscription?: any;
  planTypes: any;
  recurlyPublicKey: string;
  fullpath: any;
  context: any;
  valentinesDayVariant?: any;
  shopifyProducts?: any;
};

const GiftFlow = ({
  currencyCode,
  paymentTypes,
  giftSubscription,
  shopifyProducts,
  planTypes,
  recurlyPublicKey,
  fullpath,
  context,
  valentinesDayVariant
}: Props) => {
  const [giftFlowState, dispatchGiftFlow] = useReducer(giftFlowReducer, {
    ...initialState,
    valentinesDayVariant,
    purchasedGiftCard: giftSubscription
  });

  return (
    <Page context={context}>
      <MinimalLayout>
        {/* child styles depend upon a wrapping div */}
        <div>
          <GiftFlowContext.Provider value={{ giftFlowState, dispatchGiftFlow }}>
            <Router fullpath={fullpath}>
              <GiftFlowRouting
                planTypes={planTypes}
                currencyCode={currencyCode}
                paymentTypes={paymentTypes}
                recurlyPublicKey={recurlyPublicKey}
                shopifyProducts={shopifyProducts}
              />
            </Router>
          </GiftFlowContext.Provider>
        </div>
      </MinimalLayout>
    </Page>
  );
};

GiftFlow.defaultProps = {
  giftSubscription: null,
  valentinesDayVariant: false,
  shopifyProducts: []
};

export {
  GiftFlow,
  GiftFlowContext,
  UPDATE_GIFT_CUSTOMIZE_FORM,
  UPDATE_SERVER_ERRORS,
  UPDATE_PURCHASED_GIFT_CARD,
  RESTART_GIFT_FLOW,
  useGiftFlowContext
};
